import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  AppBar,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Restaurant as RecipeIcon,
  Inventory as IngredientIcon,
  People as UsersIcon,
  ArrowBack as BackIcon,
  MergeType as DuplicatesIcon,
  Preview as DemoIcon,
  Notifications as NotificationsIcon,
  Campaign as CreatorsIcon,
  Restaurant as ChefsIcon,
  LocalOffer as PromoCodesIcon,
  Payments as PayoutsIcon,
  Poll as SurveyIcon,
  AutoFixHigh as EnrichmentIcon,
  AddCircleOutline as SeedingIcon,
  Sync as JobsIcon,
  Image as ImageTestIcon,
  PhotoLibrary as ImageBackfillIcon,
} from '@mui/icons-material';

const drawerWidth = 240;

const navItems = [
  { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
  { label: 'Recipes', path: '/admin/recipes', icon: <RecipeIcon /> },
  { label: 'Duplicates', path: '/admin/duplicates', icon: <DuplicatesIcon /> },
  { label: 'Enrichment', path: '/admin/enrichment', icon: <EnrichmentIcon /> },
  { label: 'Seeding', path: '/admin/seeding', icon: <SeedingIcon /> },
  { label: 'Demo Meals', path: '/admin/demo-meals', icon: <DemoIcon /> },
  { label: 'Ingredients', path: '/admin/ingredients', icon: <IngredientIcon /> },
  { label: 'Users', path: '/admin/users', icon: <UsersIcon /> },
  { label: 'Creators', path: '/admin/creators', icon: <CreatorsIcon /> },
  { label: 'Chefs', path: '/admin/chefs', icon: <ChefsIcon /> },
  { label: 'Promo Codes', path: '/admin/promo-codes', icon: <PromoCodesIcon /> },
  { label: 'Payouts', path: '/admin/payouts', icon: <PayoutsIcon /> },
  { label: 'Push Notifications', path: '/admin/notifications', icon: <NotificationsIcon /> },
  { label: 'Churn Surveys', path: '/admin/churn-surveys', icon: <SurveyIcon /> },
  { label: 'Jobs & Sync', path: '/admin/jobs', icon: <JobsIcon /> },
  { label: 'Image Test', path: '/admin/image-test', icon: <ImageTestIcon /> },
  { label: 'Image Backfill', path: '/admin/image-backfill', icon: <ImageBackfillIcon /> },
];

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isSelected = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#032736',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Snackd Admin
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 1 }}>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={isSelected(item.path)}
                  onClick={() => navigate(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: '#e3f2fd',
                      borderRight: '3px solid #1976d2',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#bbdefb',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isSelected(item.path) ? '#1976d2' : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isSelected(item.path) ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Admin Dashboard v1.0
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: '#fafafa',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
